/**
 * Полоска докачки истории течений под кнопкой.
 *
 * Недостающие сутки заказывает клик по «Загрузить/Обновить», сервер отвечает
 * списком поставленных заданий (`meta.history_jobs`). Раньше об этом говорила
 * только строка в панели — работа шла втёмную, и понять, ждать ли и сколько,
 * было нельзя (фидбек владельца 2026-08-07: «нужно отображать прогресс-бар,
 * пока все данные не докачаются»).
 *
 * Правило то же, что у остальных полосок кабинета: молча не исчезает ни при
 * одном исходе. Упавшее задание завершает ожидание — за те сутки у источника
 * может не быть данных вовсе, и висеть вечно нельзя.
 *
 * Про ИСТОЧНИК говорим только по его собственному вердикту (`data_unavailable`).
 * Сорванная задача (`failed`) и неотвеченный опрос статуса — это про нас, а не
 * про CMEMS. Приговор источнику за свою неудачу полоска выносила дважды:
 * 2026-08-08 статус задачи не читался (403), 2026-08-12 задача упала на рендере
 * тайлов, уже скачав течения (16 770 узлов кадра лежали в базе). Оба раза данные
 * были на месте, а полоска говорила, что их нет.
 *
 * Подпись собирает `t()` прямо здесь: это готовая строка для полоски, а не
 * решение о том, что сказать. Форму «сутки/суток» больше не считаем руками —
 * её знает Intl.PluralRules внутри `t()`, и у английского форм две, не три.
 */
import { t } from '../i18n'

/** Ответ `/data/{task_id}/progress` в той части, что нужна полоске. */
export interface JobStatus {
  progress_percent: number
  status: string
}

export interface HistoryProgress {
  percent: number
  done: boolean
  /** Заданий, про которые так и сказал ИСТОЧНИК: данных за те сутки нет. */
  missing?: number
  /** Заданий, сорвавшихся у НАС (`failed`): течения могли и приехать — задача
   *  падает и на рендере тайлов, уже скачав их. */
  failed: number
  /** Заданий, чей статус не удалось узнать (опрос не ответил). */
  unknown: number
  total: number
}

/** Статус, которым фронт помечает неотвеченный опрос. Отдельный от 'failed':
 *  «мы не смогли спросить» и «работа провалилась» — разные новости. */
export const STATUS_UNKNOWN = 'unknown'

/** Задания считаются законченными в этих статусах (падение — тоже конец). */
const TERMINAL = new Set(['completed', 'failed', 'data_unavailable', STATUS_UNKNOWN])

export function historyProgress(jobs: JobStatus[]): HistoryProgress | null {
  if (!jobs.length) return null
  const sum = jobs.reduce((acc, j) => acc + (j.progress_percent || 0), 0)
  const done = jobs.every(j => TERMINAL.has(j.status))
  const unknown = jobs.filter(j => j.status === STATUS_UNKNOWN).length
  const missing = jobs.filter(j => j.status === 'data_unavailable').length
  const failed = jobs.filter(j => j.status === 'failed').length
  return {
    percent: done ? 100 : Math.round(sum / jobs.length),
    done,
    missing,
    failed,
    unknown,
    total: jobs.length,
  }
}

export function historyLabel(p: HistoryProgress): string {
  if (!p.done) return t('models.history.downloading', { count: p.total, percent: p.percent })
  // Пробел у источника важнее нашей неудачи: он объясняет короткие усики, и
  // повторять докачку за те сутки бессмысленно.
  if (p.missing) return t('models.history.doneMissing', { count: p.missing })
  if (p.failed) {
    // Наша неудача: задача падает и после того, как течения скачаны (рендер
    // тайлов, перезапуск сервиса). Обещать пробел в данных на этом основании
    // нельзя — повтор обычно и заканчивается успехом.
    return t('models.history.doneFailed', { count: p.failed })
  }
  if (p.unknown) {
    // Не про источник: работа могла и закончиться успешно — мы просто не смогли
    // узнать её статус. Обещать пробел в данных на этом основании нельзя.
    return t('models.history.doneUnknown', { count: p.unknown })
  }
  return t('models.history.done', { count: p.total })
}

/**
 * Заказывать ли докачку истории вместе с этим запросом слоя усиков.
 *
 * Слой перезапрашивается на каждый сдвиг карты, зум, смену глубины и вида — и
 * бэкенд ставил докачку на КАЖДЫЙ такой ответ. Снаружи это выглядело так: под
 * кнопкой сама собой шла полоска «Докачка течений за 1 сутки… 18 %» (фидбек
 * владельца 2026-08-17). Работа настоящая: сутки течений на рамку региона идут
 * отдельным процессом и минутами, заказывать их панорамированием нельзя.
 *
 * Триггер оставлен один — клик по «Загрузить/Обновить» (`armed`). Включение
 * тумблера «Траектории HSI» сюда НЕ входит, в отличие от пересчёта поля вида
 * (`shouldRecomputeHsi`, взводится и тумблером с 11.08.2026): пересчёт считает
 * по данным, которые уже лежат, а докачка тянет новые из CMEMS. Дыру при этом не
 * замалчиваем — про неё говорит подпись в панели усиков.
 *
 * `ready` — пайплайн кнопки отработал (`currentsSettled`). Пока он качает
 * запрошенную дату, дыры в истории считать не по чему: часть суток едет прямо
 * сейчас. Замок при этом НЕ снимается — ждём готовности, как это делает цепочка
 * пересчёта HSI.
 */
export function shouldFillHistory({ armed, ready }: { armed: boolean; ready: boolean }): boolean {
  return armed && ready
}

/** Шаг опроса заданий докачки, мс. Как у остальных полосок кабинета. */
export const HISTORY_POLL_MS = 2000

/** Потолок ожидания: сутки течений качаются минуты, 10 мин — с запасом. */
export const HISTORY_POLL_ATTEMPTS = 300
